import React, { memo } from 'react';
import dynamicallyResizeShopifyImageUrl from '@jbhi-fi/utils/src/resizeShopifyImage/resizeShopifyImage';

type ImageProps = {
  src: string;
  width: number;
  height?: number;
  sizes?: string;
  maxWidth?: number;
  srcSetWidths?: number[];
  className?: string;
  style?: React.CSSProperties;
  alt: string;
  lazyload?: boolean;
  flickityLazyload?: boolean;
};

const Image = memo(
  ({
    src,
    width,
    height,
    sizes,
    maxWidth,
    srcSetWidths = [160, 320, 480, 640, 800, 960, 1120, 1280, 1440, 1600, 1920, 2240, 2560],
    className,
    style,
    alt,
    lazyload = true,
    flickityLazyload = false,
  }: ImageProps): JSX.Element => {
    const imageSrc = dynamicallyResizeShopifyImageUrl(src, `${width}x${height || ''}`);
    const srcSet = [];

    srcSetWidths.forEach((imageWidth, index) => {
      if (imageWidth > maxWidth) return;
      if (width > imageWidth && width < srcSetWidths[index + 1]) {
        srcSet.push(`${imageSrc} ${width}w`);
        return;
      }
      const imageHeight = height === width ? imageWidth : '';
      srcSet.push(`${dynamicallyResizeShopifyImageUrl(src, `${imageWidth}x${imageHeight}`)} ${imageWidth}w`);
    });

    return (
      <img
        loading={lazyload ? 'lazy' : 'eager'}
        srcSet={srcSet.length > 1 && !flickityLazyload ? srcSet.join(', ') : undefined}
        data-flickity-lazyload-srcset={srcSet.length > 1 && flickityLazyload ? srcSet.join(', ') : undefined}
        sizes={sizes}
        src={!flickityLazyload ? imageSrc : undefined}
        data-flickity-lazyload-src={flickityLazyload ? imageSrc : undefined}
        alt={alt}
        className={[className, flickityLazyload ? 'flickity-lazyload' : null].join(' ')}
        style={style}
        width={width}
        height={height || width}
      />
    );
  }
);

Image.displayName = 'Image';

export default Image;
